// src/components/tournament/TableView.js

import React from 'react';
import DraggablePlayerSlot from '../common/DraggablePlayerSlot';
import { canRearrangePlayers } from '../../utils/dragDropUtils';
import { getMatchRaceTarget } from '../../utils/raceUtils';

function TableView({
    bracket,
    updateMatch,
    startMatch,
    unstartMatch,
    markFinished,
    undoFinished,
    finalizeSingleBye,
    tournamentFormat,
    onSwapPlayers,
    readOnly = false,
    hideBYEMatches = false,
    onViewProfile = null,
    playerUserIdMap = null,
    raceSettings = null, // { winnersRaceLength, losersRaceLength } — caps score entry
    isDoubles = false // If true, abbreviate each partner to "First L." in team names
}) {
    // Check tournament type
    const isSingleElimination = bracket.some(m => m.bracket === 'SE');
    const isModifiedSingleElimination = bracket.some(m => m.bracket === 'MSE');
    const isRoundRobin = bracket.some(m => m.bracket === 'RR');

    // Helper function to check if a match is a BYE match
    const isBYEMatch = (match) => {
        return match.p1?.name === "BYE" || match.p2?.name === "BYE";
    };

    // Filter function that optionally excludes BYE matches
    const filterMatches = (matches) => {
        return hideBYEMatches ? matches.filter(m => !isBYEMatch(m)) : matches;
    };

    // Group matches by bracket type and sort by match number
    const winnersMatches = filterMatches(bracket.filter(m => m.bracket === 'W')).sort((a, b) => a.matchNum - b.matchNum);
    const losersMatches = filterMatches(bracket.filter(m => m.bracket === 'L')).sort((a, b) => a.matchNum - b.matchNum);
    const grandFinalsMatches = filterMatches(bracket.filter(m => m.bracket === 'GF')).sort((a, b) => a.matchNum - b.matchNum);
    const singleElimMatches = filterMatches(bracket.filter(m => m.bracket === 'SE')).sort((a, b) => a.matchNum - b.matchNum);
    const mseMatches = filterMatches(bracket.filter(m => m.bracket === 'MSE')).sort((a, b) => a.matchNum - b.matchNum);
    const roundRobinMatches = filterMatches(bracket.filter(m => m.bracket === 'RR')).sort((a, b) => a.matchNum - b.matchNum);

    const renderMatchRow = (match) => {
        const { p1, p2, score1, score2, finished, matchNum, table, winner, bracket: bracketType, round, p1LiveScore, p2LiveScore } = match;

        // Cap manual score entry at this match's race-to target (winners vs losers).
        const raceTarget = getMatchRaceTarget(match, raceSettings);

        // Use live score if available and match is in progress, otherwise use final score
        // With player-specific scoring, prefer the most recent update from either player
        const getLiveScore = () => {
            if (!match.started || finished) return null;
            // If both players have submitted, they should have the same scores
            // Use p1's view first, fall back to p2's
            if (p1LiveScore) return p1LiveScore;
            if (p2LiveScore) return p2LiveScore;
            return null;
        };
        const liveScore = getLiveScore();
        const displayScore1 = liveScore ? liveScore.p1 : score1;
        const displayScore2 = liveScore ? liveScore.p2 : score2;

        const isReady = (
            !finished &&
            p1 && p2 &&
            typeof p1.name === "string" &&
            typeof p2.name === "string" &&
            !["Winner of", "Loser of"].some(prefix => p1.name.startsWith(prefix) || p2.name.startsWith(prefix)) &&
            !["BYE"].includes(p1.name) &&
            !["BYE"].includes(p2.name) &&
            p1.name && p2.name
        );

        // Check if this is a BYE match that can be finalized
        const isByeMatch = p1.name === "BYE" || p2.name === "BYE";
        const canFinalizeBye = (
            !finished &&
            isByeMatch &&
            finalizeSingleBye && // Only if individual BYE finalization is available
            ((p1.name === "BYE" && p2.name && p2.name !== "BYE" && !p2.name.includes("Winner of") && !p2.name.includes("Loser of")) ||
             (p2.name === "BYE" && p1.name && p1.name !== "BYE" && !p1.name.includes("Winner of") && !p1.name.includes("Loser of")))
        );

        const canFinalize = canFinalizeBye || (
            !finished &&
            !isByeMatch &&
            score1 !== "" &&
            score2 !== "" &&
            !isNaN(score1) &&
            !isNaN(score2) &&
            Number(score1) !== Number(score2)
        );

        // Function to handle finalization based on match type
        const handleFinalize = () => {
            if (canFinalizeBye) {
                finalizeSingleBye(match);
            } else {
                markFinished(match);
            }
        };

        // Check if match has pending players (references to other matches)
        const hasPendingPlayers = (
            (p1.name && (p1.name.startsWith("Winner of") || p1.name.startsWith("Loser of"))) ||
            (p2.name && (p2.name.startsWith("Winner of") || p2.name.startsWith("Loser of")))
        );

        // Disable score inputs for finished matches or matches with pending players
        const scoresDisabled = finished || hasPendingPlayers;

        // Determine match state for border color
        // Returns CSS variable names for theming support
        const getMatchBorderColor = () => {
            if (finished) {
                return "var(--match-finished)"; // Black - finished
            }

            // Check if match has been started
            if (match.started) {
                return "var(--match-playing)"; // Blue - playing (started)
            }

            // Check if match has both players (not pending, not BYE, not empty)
            const hasBothPlayers = (
                p1 && p2 &&
                typeof p1.name === "string" &&
                typeof p2.name === "string" &&
                !["Winner of", "Loser of"].some(prefix => p1.name.startsWith(prefix) || p2.name.startsWith(prefix)) &&
                !["BYE"].includes(p1.name) &&
                !["BYE"].includes(p2.name) &&
                p1.name && p2.name
            );

            if (hasBothPlayers) {
                return "var(--match-waiting)"; // Orange - waiting (both players ready, not started)
            }

            // Pending - waiting for players
            return "var(--match-pending)"; // Gray - pending
        };

        const renderPlayer = (player, scoreField, playerSlot, displayScore) => {
            const isPlaceholder = typeof player.name !== "string" ||
                player.name.startsWith("Winner of") ||
                player.name.startsWith("Loser of") ||
                player.name === "BYE" ||
                !player.name;

            const isMobile = window.innerWidth <= 768;

            if (isPlaceholder) {
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '8px' }}>
                        <span style={{
                            color: "var(--text-muted)",
                            fontSize: isMobile ? 12 : 14,
                            fontStyle: 'italic',
                            minWidth: isMobile ? '80px' : '120px',
                            textAlign: 'center',
                            display: 'inline-block'
                        }}>
                            {player.name || "TBD"}
                        </span>
                        <span style={{ color: 'var(--border-color)', minWidth: '30px', textAlign: 'center' }}>-</span>
                    </div>
                );
            }

            const canRearrange = match.round === 1 && canRearrangePlayers(bracket);
            const isWinner = finished && winner === player.name;

            // Get player's user ID from the map if available
            const playerUserId = playerUserIdMap?.get(player.name)?.odUserId || null;

            return (
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '1px' : '2px' }}>
                    <DraggablePlayerSlot
                        playerName={player.name}
                        isWinner={isWinner}
                        isFinished={finished}
                        onSwapPlayers={readOnly ? () => {} : onSwapPlayers}
                        matchId={match.id}
                        playerSlot={playerSlot}
                        canRearrange={readOnly ? false : canRearrange}
                        onViewProfile={onViewProfile}
                        playerUserId={playerUserId}
                        isDoubles={isDoubles}
                        style={{
                            minWidth: isMobile ? '80px' : '120px',
                            fontSize: isMobile ? 12 : 14
                        }}
                    />
                    {readOnly ? (
                        // Read-only: Just show the score (prefer displayScore for live updates)
                        <div style={{
                            minWidth: '30px',
                            textAlign: 'center',
                            fontSize: isMobile ? 12 : 14,
                            fontWeight: 'bold',
                            color: finished && winner === player.name ? 'var(--success)' : 'var(--text-primary)'
                        }}>
                            {displayScore ?? match[scoreField]}
                        </div>
                    ) : (
                        // Editable: Show score with +/- buttons
                        <div style={{
                            display: 'flex',
                            width: isMobile ? 39 : 48,
                            height: isMobile ? 22 : 24,
                            border: 'none',
                            borderRadius: 3,
                            overflow: 'hidden',
                            backgroundColor: finished && winner === player.name ? 'var(--success-light)' : (scoresDisabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)'),
                            boxShadow: '0 0 0 1px var(--border-color)'
                        }}>
                            <button
                                style={{
                                    flex: '0 0 auto',
                                    width: isMobile ? 13 : 16,
                                    height: '100%',
                                    border: 'none',
                                    background: scoresDisabled ? 'var(--bg-disabled)' : 'var(--danger-light)',
                                    color: scoresDisabled ? 'var(--btn-secondary)' : 'var(--danger)',
                                    fontSize: isMobile ? 10 : 11,
                                    fontWeight: 'bold',
                                    cursor: scoresDisabled ? 'default' : 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => !scoresDisabled && updateMatch(match.id, { [scoreField]: Math.max(0, Number(match[scoreField]) - 1).toString() })}
                                disabled={scoresDisabled}
                                title="Decrease score"
                            >
                                −
                            </button>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isMobile ? 12 : 14,
                                color: scoresDisabled ? 'var(--btn-secondary)' : 'var(--text-primary)',
                                backgroundColor: finished && winner === player.name ? 'var(--success-light)' : (scoresDisabled ? 'var(--bg-tertiary)' : 'var(--bg-secondary)')
                            }}>
                                {displayScore ?? match[scoreField]}
                            </div>
                            <button
                                style={{
                                    flex: '0 0 auto',
                                    width: isMobile ? 13 : 16,
                                    height: '100%',
                                    border: 'none',
                                    background: scoresDisabled ? 'var(--bg-disabled)' : 'var(--success-light)',
                                    color: scoresDisabled ? 'var(--btn-secondary)' : 'var(--success)',
                                    fontSize: isMobile ? 10 : 11,
                                    fontWeight: 'bold',
                                    cursor: scoresDisabled ? 'default' : 'pointer',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                onClick={() => {
                                    if (scoresDisabled) return;
                                    const next = Number(match[scoreField]) + 1;
                                    const capped = raceTarget && raceTarget > 0 ? Math.min(raceTarget, next) : next;
                                    updateMatch(match.id, { [scoreField]: capped.toString() });
                                }}
                                disabled={scoresDisabled || (raceTarget > 0 && Number(match[scoreField]) >= raceTarget)}
                                title="Increase score"
                            >
                                +
                            </button>
                        </div>
                    )}
                </div>
            );
        };

        const isMobile = window.innerWidth <= 768;

        return (
            <tr
                key={match.id}
                style={{
                    backgroundColor: finished ? 'var(--bg-tertiary)' : isReady ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'var(--border-color)',
                    borderLeftWidth: '5px',
                    borderLeftColor: getMatchBorderColor()
                }}
            >
                <td style={{ padding: isMobile ? '4px' : '8px', textAlign: 'center', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                    {matchNum}
                </td>
                <td style={{ padding: isMobile ? '4px' : '8px', textAlign: 'center' }}>
                    <span style={{
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: isMobile ? '10px' : '12px',
                        fontWeight: 'bold',
                        color: 'var(--text-on-dark)',
                        backgroundColor:
                            bracketType === 'W' ? 'var(--bracket-winners)' :
                                bracketType === 'L' ? 'var(--bracket-losers)' :
                                    bracketType === 'SE' ? 'var(--bracket-single-elim)' :
                                        bracketType === 'RR' ? 'var(--bracket-round-robin)' :
                                            'var(--bracket-grand-finals)'
                    }}>
                        {bracketType === 'W' ? `W-R${round}` :
                            bracketType === 'L' ? `L-R${round}` :
                                bracketType === 'SE' ? `R${round}` :
                                    bracketType === 'RR' ? `R${round}` :
                                        bracketType === 'GF' ? 'GF' : bracketType}
                    </span>
                </td>
                <td style={{ padding: isMobile ? '4px' : '8px' }}>
                    {renderPlayer(p1, 'score1', 'p1', displayScore1)}
                </td>
                <td style={{ padding: isMobile ? '4px' : '8px' }}>
                    {renderPlayer(p2, 'score2', 'p2', displayScore2)}
                </td>
                <td style={{ padding: isMobile ? '4px' : '8px', textAlign: 'center', fontVariantNumeric: 'slashed-zero' }}>
                    {readOnly || finished || match.started ? (
                        <span style={{
                            fontSize: isMobile ? 12 : 14,
                            fontWeight: 'bold',
                            color: 'var(--text-primary)'
                        }}>
                            {table || '-'}
                        </span>
                    ) : (
                        <input
                            style={{
                                width: isMobile ? 40 : 50,
                                fontSize: isMobile ? 12 : 14,
                                border: '1px solid var(--border-color)',
                                borderRadius: 3,
                                textAlign: 'center',
                                padding: '2px 4px'
                            }}
                            // Uncontrolled + key so the whole number can be typed
                            // before committing on blur/Enter. Committing per-
                            // keystroke would reject an intermediate "1" while
                            // table 1 is in use. `key` remounts the input when the
                            // committed value changes externally (assignment/sync).
                            key={table}
                            defaultValue={table}
                            onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                            onBlur={e => {
                                const next = e.target.value.trim();
                                if (next === (table || '')) return;
                                // Conflict validation (incl. in-progress table guard)
                                // lives in updateMatch, which reads fresh bracket
                                // state. See AppWithFirebase.updateMatch.
                                const ok = updateMatch(match.id, { table: next });
                                if (ok === false) e.target.value = table || ''; // rejected — revert
                            }}
                            maxLength={5}
                            placeholder={isMobile ? "#" : "Table"}
                        />
                    )}
                </td>
                <td style={{ padding: isMobile ? '4px' : '8px', textAlign: 'center' }}>
                    {(() => {
                        // Use the same logic as border colors
                        const borderColor = getMatchBorderColor();
                        let statusText, bgColor;

                        if (borderColor === 'var(--match-finished)') {
                            // Finished - Black
                            statusText = 'Finished';
                            bgColor = 'var(--match-finished)';
                        } else if (borderColor === 'var(--match-playing)') {
                            // Playing - Blue
                            statusText = 'Playing';
                            bgColor = 'var(--match-playing)';
                        } else if (borderColor === 'var(--match-waiting)') {
                            // Waiting - Orange
                            statusText = 'Waiting';
                            bgColor = 'var(--match-waiting)';
                        } else {
                            // Pending Players - Gray
                            statusText = 'Pending';
                            bgColor = 'var(--match-pending)';
                        }

                        return (
                            <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: isMobile ? '10px' : '12px',
                                fontWeight: 'bold',
                                color: 'var(--text-on-dark)',
                                backgroundColor: bgColor
                            }}>
                                {statusText}
                            </span>
                        );
                    })()}
                </td>
                {!readOnly && (
                    <td style={{ padding: isMobile ? '4px' : '8px', textAlign: 'center' }}>
                        {/* Start Match button - for ready matches that haven't started */}
                        {isReady && !match.started && !finished && (
                            <button
                                style={{
                                    fontSize: isMobile ? 10 : 12,
                                    padding: isMobile ? "3px 6px" : "4px 8px",
                                    borderRadius: 4,
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: "var(--text-on-dark)",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 'bold',
                                    marginRight: isMobile ? '2px' : '4px'
                                }}
                                onClick={() => startMatch(match.id)}
                                title="Start this match"
                            >
                                {isMobile ? '▶' : '▶ Start'}
                            </button>
                        )}

                        {/* Unstart button - for started matches that aren't finished */}
                        {match.started && !finished && (
                            <button
                                style={{
                                    fontSize: isMobile ? 10 : 12,
                                    padding: isMobile ? "3px 6px" : "4px 8px",
                                    borderRadius: 4,
                                    backgroundColor: "var(--warning)",
                                    color: "var(--text-on-dark)",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 'bold',
                                    marginRight: isMobile ? '2px' : '4px'
                                }}
                                onClick={() => unstartMatch(match.id)}
                                title="Unstart this match"
                            >
                                {isMobile ? '⏸' : '⏸ Unstart'}
                            </button>
                        )}

                        {canFinalize && (
                            <button
                                style={{
                                    fontSize: isMobile ? 10 : 12,
                                    padding: isMobile ? "3px 6px" : "4px 8px",
                                    borderRadius: 4,
                                    backgroundColor: canFinalizeBye ? "var(--warning)" : "var(--success)",
                                    color: "var(--text-on-dark)",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 'bold',
                                    marginRight: isMobile ? '2px' : '4px'
                                }}
                                onClick={handleFinalize}
                                title={canFinalizeBye ? "Finalize this BYE match" : "Finalize this match"}
                            >
                                {canFinalizeBye
                                    ? (isMobile ? '⚡' : '⚡ Advance')
                                    : (isMobile ? '✓' : '✓ Finish')
                                }
                            </button>
                        )}

                        {finished && (
                            <button
                                style={{
                                    fontSize: isMobile ? 10 : 12,
                                    padding: isMobile ? "3px 6px" : "4px 8px",
                                    borderRadius: 4,
                                    backgroundColor: "var(--danger)",
                                    color: "var(--text-on-dark)",
                                    border: "none",
                                    cursor: "pointer",
                                    fontWeight: 'bold'
                                }}
                                onClick={() => undoFinished(match)}
                                title="Undo this match"
                            >
                                {isMobile ? '↶' : '↶ Undo'}
                            </button>
                        )}
                    </td>
                )}
            </tr>
        );
    };

    const renderSection = (title, matches, bgColor, sectionId) => {
        if (matches.length === 0) return null;

        const isMobile = window.innerWidth <= 768;

        return (
            <div style={{ marginBottom: '30px' }} data-section={sectionId}>
                <h3 style={{
                    backgroundColor: bgColor,
                    color: 'var(--text-on-dark)',
                    padding: '8px 16px',
                    margin: '0 0 10px 0',
                    borderRadius: '6px',
                    fontWeight: 'bold'
                }}>
                    {title} ({matches.length} matches)
                </h3>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{
                        width: '100%',
                        minWidth: isMobile ? '800px' : '100%',
                        borderCollapse: 'collapse',
                        backgroundColor: 'var(--bg-secondary)',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        boxShadow: 'var(--shadow-sm)',
                        tableLayout: isMobile ? 'auto' : 'fixed'
                    }}>
                        <thead>
                            <tr style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                                <th style={{
                                    padding: isMobile ? '8px 4px' : '12px 8px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'var(--text-secondary)',
                                    width: isMobile ? 'auto' : '80px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>Match #</th>
                                <th style={{
                                    padding: isMobile ? '8px 4px' : '12px 8px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'var(--text-secondary)',
                                    width: isMobile ? 'auto' : '100px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>Round</th>
                                <th style={{
                                    padding: isMobile ? '8px 4px' : '12px 8px',
                                    textAlign: 'left',
                                    fontWeight: 'bold',
                                    color: 'var(--text-secondary)',
                                    width: isMobile ? 'auto' : '200px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>Player 1 & Score</th>
                                <th style={{
                                    padding: isMobile ? '8px 4px' : '12px 8px',
                                    textAlign: 'left',
                                    fontWeight: 'bold',
                                    color: 'var(--text-secondary)',
                                    width: isMobile ? 'auto' : '200px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>Player 2 & Score</th>
                                <th style={{
                                    padding: isMobile ? '8px 4px' : '12px 8px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'var(--text-secondary)',
                                    width: isMobile ? 'auto' : '80px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>Table</th>
                                <th style={{
                                    padding: isMobile ? '8px 4px' : '12px 8px',
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    color: 'var(--text-secondary)',
                                    width: isMobile ? 'auto' : '100px',
                                    fontSize: isMobile ? '12px' : '14px'
                                }}>Status</th>
                                {!readOnly && (
                                    <th style={{
                                        padding: isMobile ? '8px 4px' : '12px 8px',
                                        textAlign: 'center',
                                        fontWeight: 'bold',
                                        color: 'var(--text-secondary)',
                                        width: isMobile ? 'auto' : '120px',
                                        fontSize: isMobile ? '12px' : '14px'
                                    }}>Actions</th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {matches.map(renderMatchRow)}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div style={{
            padding: '20px 20px 20px 40px',
            minHeight: '100vh'
        }}>
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto'
            }}>
                {isRoundRobin ? (
                    // Round Robin view - group by round
                    (() => {
                        // Group matches by round
                        const matchesByRound = {};
                        roundRobinMatches.forEach(match => {
                            const round = match.round || 1;
                            if (!matchesByRound[round]) {
                                matchesByRound[round] = [];
                            }
                            matchesByRound[round].push(match);
                        });

                        // Render each round as a separate section
                        const rounds = Object.keys(matchesByRound).sort((a, b) => Number(a) - Number(b));
                        return rounds.map(round => (
                            <React.Fragment key={`roundrobin-r${round}`}>
                                {renderSection(
                                    `Round ${round}`,
                                    matchesByRound[round],
                                    'var(--bracket-round-robin)',
                                    `roundrobin-r${round}`
                                )}
                            </React.Fragment>
                        ));
                    })()
                ) : isSingleElimination ? (
                    // Single elimination view
                    renderSection('Single Elimination Bracket', singleElimMatches, 'var(--bracket-single-elim)', 'singleelim')
                ) : isModifiedSingleElimination ? (
                    // Modified Single Elimination view
                    renderSection('Modified Single Elimination (APA)', mseMatches, 'var(--bracket-winners)', 'mse')
                ) : (
                    // Double elimination view
                    <>
                        {renderSection('Winners Bracket', winnersMatches, 'var(--bracket-winners)', 'winners')}
                        {renderSection('Losers Bracket', losersMatches, 'var(--bracket-losers)', 'losers')}
                        {renderSection('Grand Finals', grandFinalsMatches, 'var(--bracket-grand-finals)', 'grandfinals')}
                    </>
                )}

                {bracket.length === 0 && (
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        color: 'var(--btn-secondary)'
                    }}>
                        <h3>No Tournament Active</h3>
                        <p>Start a tournament to view matches in table format.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

// Memoized: props from App are stable values plus stable-identity callbacks, so the
// table only re-renders when the bracket data or display flags actually change.
export default React.memo(TableView);
